import { useMemo } from 'react'
import { motion } from 'framer-motion'
import { Search, Zap, Trash2, Eye, RefreshCw, Loader2 } from 'lucide-react'
import type { BdrLead } from '@/types'

function getTemperature(score: number | null | undefined): { label: string; color: string } {
  const s = score ?? 0
  if (s >= 80) return { label: 'Quente', color: '#ef4444' }
  if (s >= 50) return { label: 'Morno', color: '#f59e0b' }
  return { label: 'Frio', color: '#0ea5e9' }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface BdrNurturingTableProps {
  nurturingLeads: BdrLead[]
  loadingNurturing: boolean
  filters: {
    nurturingSearch: string
    setNurturingSearch: (v: string) => void
  }
  fetchNurturingLeads: () => void
  handleRecoverLead: (leadId: string) => void
  handleDiscardLead: (leadId: string) => void
  openLeadDetail: (lead: BdrLead) => void
}

// ─── Component ───────────────────────────────────────────────────────────────

export function BdrNurturingTable({
  nurturingLeads,
  loadingNurturing,
  filters,
  fetchNurturingLeads,
  handleRecoverLead,
  handleDiscardLead,
  openLeadDetail,
}: BdrNurturingTableProps) {
  const filteredNurturing = useMemo(() => {
    if (!filters.nurturingSearch.trim()) return nurturingLeads
    const q = filters.nurturingSearch.toLowerCase()
    return nurturingLeads.filter(
      (l) =>
        l.empresa.toLowerCase().includes(q) ||
        l.contato.toLowerCase().includes(q) ||
        (l.segmento && l.segmento.toLowerCase().includes(q)),
    )
  }, [nurturingLeads, filters.nurturingSearch])

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* ── Toolbar ────────────────────────────────────────────────────── */}
      <div className='glass-card border-white/[0.06] p-4 mb-4'>
        <div className='flex items-center gap-2'>
          <div className='relative flex-1 min-w-[200px]'>
            <Search
              size={14}
              className='absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none'
            />
            <input
              type='text'
              value={filters.nurturingSearch}
              onChange={(e) => filters.setNurturingSearch(e.target.value)}
              placeholder='Buscar em nutricao...'
              className='w-full bg-white/[0.04] border border-white/[0.06] text-text-primary text-xs rounded-lg pl-9 pr-3 py-2 outline-none focus:border-accent transition-colors min-h-[44px] placeholder:text-text-muted'
            />
          </div>
          <button
            onClick={fetchNurturingLeads}
            className='min-w-[44px] min-h-[44px] flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-text-muted hover:text-text-primary hover:bg-white/[0.04] transition-colors'
          >
            <RefreshCw size={14} />
            Atualizar
          </button>
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <div className='glass-card border-white/[0.06] overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='w-full text-xs'>
            <thead>
              <tr className='border-b border-white/[0.06]'>
                <th className='text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                  Empresa
                </th>
                <th className='text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                  Contato
                </th>
                <th className='text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                  Segmento
                </th>
                <th className='text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                  Temperatura
                </th>
                <th className='text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                  Servico de Interesse
                </th>
                <th className='text-left px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                  Entrada
                </th>
                <th className='text-right px-3 py-2.5 text-text-muted font-medium uppercase tracking-wider text-[10px]'>
                  Acoes
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingNurturing ? (
                <tr>
                  <td colSpan={7} className='text-center py-12'>
                    <Loader2 size={20} className='animate-spin text-accent mx-auto' />
                  </td>
                </tr>
              ) : filteredNurturing.length === 0 ? (
                <tr>
                  <td colSpan={7} className='text-center py-12 text-text-muted'>
                    Nenhum lead em nutricao.
                  </td>
                </tr>
              ) : (
                filteredNurturing.map((lead) => (
                  <tr
                    key={lead.id}
                    className='border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors'
                  >
                    {/* Empresa */}
                    <td className='px-3 py-2.5'>
                      <div className='flex items-center gap-2.5'>
                        <div className='w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold text-sm shrink-0'>
                          {getInitials(lead.empresa)}
                        </div>
                        <span className='text-text-primary font-medium truncate'>
                          {lead.empresa}
                        </span>
                      </div>
                    </td>

                    {/* Contato */}
                    <td className='px-3 py-2.5'>
                      <div className='text-text-primary truncate'>{lead.contato}</div>
                      {lead.cargo && (
                        <div className='text-text-muted text-[10px] truncate'>{lead.cargo}</div>
                      )}
                    </td>

                    {/* Segmento */}
                    <td className='px-3 py-2.5 text-text-muted'>{lead.segmento || '-'}</td>

                    {/* Temperatura */}
                    <td className='px-3 py-2.5'>
                      {(() => {
                        const temp = getTemperature(lead.score)
                        return (
                          <span
                            className='text-[10px] font-medium px-1.5 py-0.5 rounded-full'
                            style={{ color: temp.color, backgroundColor: `${temp.color}18` }}
                          >
                            {temp.label}
                          </span>
                        )
                      })()}
                    </td>

                    {/* Servico de Interesse */}
                    <td className='px-3 py-2.5 text-text-muted'>
                      {lead.servicos?.join(', ') || lead.problema || '-'}
                    </td>

                    {/* Entrada */}
                    <td className='px-3 py-2.5 text-text-muted whitespace-nowrap'>
                      {formatDate(lead.dataEntrada)}
                    </td>

                    {/* Acoes */}
                    <td className='px-3 py-2.5'>
                      <div className='flex items-center justify-end gap-1'>
                        <button
                          onClick={() => handleRecoverLead(lead.id)}
                          title='Recuperar lead'
                          className='min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-[#10B981]/10 transition-colors'
                        >
                          <Zap size={15} className='text-[#10B981]' />
                        </button>
                        <button
                          onClick={() => handleDiscardLead(lead.id)}
                          title='Descartar'
                          className='min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-[#ef4444]/10 transition-colors'
                        >
                          <Trash2 size={15} className='text-[#ef4444]' />
                        </button>
                        <button
                          onClick={() => openLeadDetail(lead)}
                          title='Ver detalhes'
                          className='min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-white/[0.06] transition-colors'
                        >
                          <Eye size={15} className='text-text-muted' />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {!loadingNurturing && (
          <div className='px-4 py-2 border-t border-white/[0.04] text-text-muted text-[10px]'>
            {filteredNurturing.length} leads em nutricao
          </div>
        )}
      </div>
    </motion.div>
  )
}
